import { HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { retryStrategyV2 } from '@personal-area/common';

import {
  AuthMethod,
  AuthToken,
  AuthTokenResponse,
  CabinetAuthToken,
  LoginData,
  LoginForm,
  LoginMethod,
  tokenKey,
} from '@personal-area/global/authorization/models';
import { RESUBMIT_FORM } from '@personal-area/global/captcha';
import { Role } from '@personal-area/global/users/models';
import { CryptoStorageService, UniversalStorageService } from '@personal-area/storage';
import { IS_API_GATEWAY } from '@personal-area/global/http/models';
import { SocialNetworksEnum } from '@personal-area/global/social-authorization/models';

import {
  catchError,
  concatMap,
  delay,
  first,
  map,
  Observable,
  retry,
  Subject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { jwtExpiresAt } from './jwt';
import { OauthApiService } from './oauth.api.service';

const authTokenFromDTO = (dto: AuthTokenResponse): AuthToken => ({
  ...dto,
  lastRoles: dto.lastRoles || [],
  usedAccountTypes: [],
  accessExpiresAt: jwtExpiresAt(dto.accessToken, true),
  refreshExpiresAt: jwtExpiresAt(dto.refreshToken, false),
});

export interface ServerTime {
  serverTimestamp: number;
  clientTimestamp: number;
}

const loginMethodFromEmail = (email: string): LoginMethod =>
  ['facebook', 'google', 'apple'].includes(email as LoginMethod)
    ? email as LoginMethod
    : 'email';

@Injectable({ providedIn: 'root' })
export class AuthService {
  public cabinetRoles = [Role.Trader, Role.Partner, Role.TradingPlatform];
  public loginMethod$ = new Subject<LoginMethod>();

  private _onLogin = new Subject<LoginData>();
  private _onError = new Subject<HttpErrorResponse>();
  private _onLogout = new Subject<void>();
  private _serverTime?: ServerTime;

  constructor(
    private _router: Router,
    private _storageService: UniversalStorageService,
    private _oauthService: OauthApiService,
    private _cryptoStorageService: CryptoStorageService,
  ) {
  }

  public get onLogin(): Observable<LoginData> {
    return this._onLogin.asObservable();
  }

  public get onLogout(): Observable<void> {
    return this._onLogout.asObservable();
  }

  public get onError(): Observable<any> {
    return this._onError.asObservable();
  }

  public get userId(): number {
    return this.authToken ? this.authToken.userID : 0;
  }

  public get currentTimestampByServerTime(): number {
    const serverTime = this._serverTime;

    if (serverTime) {
      if (Date.now() < serverTime.clientTimestamp) {
        return serverTime.serverTimestamp;
      }

      return serverTime.serverTimestamp + (Date.now() - serverTime.clientTimestamp)
    }

    return Date.now();
  }

  public get isLoggedIn(): boolean {
    const token = this.authToken;

    return (
      (token?.userID && !this.isRefreshTokenExpired) || false
    );
  }

  public get isAccessTokenExpired(): boolean {
    return (this.authToken?.accessExpiresAt ?? 0) < this.currentTimestampByServerTime;
  }

  public get isRefreshTokenExpired(): boolean {
    return (this.authToken?.refreshExpiresAt ?? 0) < this.currentTimestampByServerTime;
  }

  public get authToken(): AuthToken | null {
    return JSON.parse(this._cryptoStorageService.getItem(tokenKey) || 'null');
  }

  public set authToken(token: AuthToken | null) {
    if (token === null) {
      this._cryptoStorageService.removeItem(tokenKey);
      this._storageService.removeHttpOnlyItem('is_auth').subscribe();
      return;
    }

    this.saveAuthToken(token);
    this._storageService
      .setHttpOnlyItem('is_auth', '1', {
        expires: token.accessExpiresAt / 1000,
      })
      .subscribe();
  }

  public get hasCabinetAccounts(): boolean {
    return Boolean(
      this.authToken &&
      this.authToken.usedAccountTypes.some((r) => this.cabinetRoles.includes(r)),
    );
  }

  public get hasTraderAccounts(): boolean {
    return Boolean(
      this.authToken &&
      this.authToken.usedAccountTypes.some((r) => Role.Trader === r),
    );
  }

  public get hasMobileApp(): boolean {
    return Boolean(
      this.authToken &&
      this.authToken.lastRoles.some((r) =>
        [Role.Investor, Role.TradingPlatform, Role.Trader].includes(r),
      ),
    );
  }

  /**
   * Проверка роли токена, может отличаться от роли пользователя
   */
  get isCabinetRole(): boolean {
    if (!this.authToken) {
      return false;
    }

    return this.cabinetRoles.includes(this.authToken.role);
  }

  public saveAuthToken(token: AuthToken): void {
    this._cryptoStorageService.setItem(tokenKey, JSON.stringify(token));
  }

  public setServerTime(dateTime: string) {
    const serverTimestamp = Date.parse(dateTime);

    if (!isNaN(serverTimestamp)) {
      this._serverTime = {
        serverTimestamp,
        clientTimestamp: Date.now(),
      }
    }
  }

  private _afterLogin = (token: AuthToken, isLoginAfterReg = false) => {
    return this._oauthService.getUsedAccountTypes(token.userID).pipe(
      map((usedAccountTypes) => ({ ...token, usedAccountTypes })),
      delay(300),
      tap((token) => {
        this.saveAuthToken(token);
        this._onLogin.next({ ...token, isLoginAfterReg });
      }),
    );
  };

  public login(
    loginData: LoginForm,
    isLoginAfterReg: boolean,
    method: AuthMethod,
    headers: HttpHeaders,
  ): Observable<AuthToken> {
    loginData.email = loginData?.email.trim();

    const context =
      method === AuthMethod.Social
        ? new HttpContext()
        : new HttpContext().set(RESUBMIT_FORM, {
          type: 'login',
          form: loginData,
        });

    context.set(IS_API_GATEWAY, true);

    return this._oauthService.requestCode(context).pipe(
      concatMap((code) =>
        this._oauthService.requestTokenV2(
          method,
          context,
          headers,
          code,
          method === AuthMethod.Password ? loginData : undefined,
          method === AuthMethod.Social ? {
            socialToken: loginData.password,
            type: loginData.email as SocialNetworksEnum,
          } : undefined,
        ),
      ),
      map(authTokenFromDTO),
      tap((token) => {
        this.authToken = token;
        this.loginMethod$.next(loginMethodFromEmail(loginData.email));
      }),
      switchMap((token) => this._afterLogin(token, isLoginAfterReg)),
      catchError((err) => {
        this._onError.next(err);
        return throwError(() => err);
      }),
    );
  }

  public loginViaCookie(cookieToken: CabinetAuthToken): Observable<AuthToken> {
    const token = authTokenFromDTO({
      expiresIn: 3600,
      lastRoles: [],
      tokenType: 'Bearer',
      accessToken: cookieToken.accessToken,
      refreshToken: cookieToken.refreshToken,
      role: cookieToken.role || Role.Trader,
      userID: Number(cookieToken.userId),
    });

    this.authToken = token;
    return this._afterLogin(token).pipe(
      catchError((err) => {
        this._onError.next(err);
        return throwError(() => err);
      }),
      tap(() => {
        if (!this.isLoggedIn) {
          throwError(() => 'Not authorized');
        }
      }),
    );
  }

  public logout(redirect = true): void {
    if (redirect) {
      /* Need onLogout.subscribe because navigate(['/login']) and render login page
      could be done faster then other onLogout subscribtions could be invoked */
      this.onLogout.pipe(first()).subscribe(() => {
        this._router.navigate(['/login']);
      });
    }
    this.authToken = null;
    this._onLogout.next();
  }

  public refreshAuthToken(): Observable<AuthToken> {
    const prevToken = this.authToken;

    if (!prevToken) {
      throw new Error('empty token');
    }

    return this._oauthService.requestRefreshToken(prevToken.refreshToken).pipe(
      retry({
        delay: (err, i) => retryStrategyV2(err, i, {
          duration: 2000,
          isScalingDuration: false,
        }),
      }),
      tap((token) => {
        if (!token) {
          throwError(() => 'empty token');
        }
      }),
      map((token) => ({
        ...authTokenFromDTO(token),
        usedAccountTypes: prevToken.usedAccountTypes || [],
      })),
      tap(token => this.authToken = token),
    );
  }
}
